import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Users, Crown, Zap, Trash2, Key, Copy, Eye, EyeOff, Search, TrendingUp, Calendar, RefreshCw, Palette, Pencil, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { KompaktBrandingEditor } from './KompaktBrandingEditor';

interface KompaktUser {
  id: string;
  email: string;
  plan: string;
  daily_limit: number;
  created_at: string;
  updated_at: string;
}

interface KompaktApiKey {
  id: string;
  api_key: string;
  user_id: string;
  created_at: string;
}

interface UserStats {
  totalAnalyses: number;
  todayUsage: number;
}

interface UserWithStats extends KompaktUser {
  stats?: UserStats;
  apiKeys?: KompaktApiKey[];
}

// API Keys Cell Component
function ApiKeysCell({ apiKeys }: { apiKeys?: KompaktApiKey[] }) {
  const [showKeys, setShowKeys] = useState(false);
  const { toast } = useToast();

  const copyToClipboard = (key: string) => {
    navigator.clipboard.writeText(key);
    toast({
      title: 'Kopiert!',
      description: 'API-Key wurde in die Zwischenablage kopiert.',
    });
  };

  if (!apiKeys || apiKeys.length === 0) {
    return <span className="text-xs text-muted-foreground">Keine Keys</span>;
  }

  return (
    <div className="space-y-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowKeys(!showKeys)}
        className="h-7 text-xs"
      >
        {showKeys ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
        {apiKeys.length} Key{apiKeys.length > 1 ? 's' : ''}
      </Button>
      {showKeys && (
        <div className="space-y-1 max-w-xs">
          {apiKeys.map((key) => (
            <div key={key.id} className="flex items-center gap-1 bg-muted p-1 rounded text-xs">
              <code className="flex-1 truncate font-mono text-[10px]">
                {key.api_key.substring(0, 20)}...
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(key.api_key)}
                className="h-5 w-5 p-0"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function KompaktAdmin() {
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<UserWithStats | null>(null);
  const [editPlan, setEditPlan] = useState<'free' | 'starter' | 'pro' | 'admin'>('free');
  const [editDailyLimit, setEditDailyLimit] = useState(3);
  const [savingUser, setSavingUser] = useState(false);
  const { toast } = useToast();

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Fetch all kompakt users
      const { data: usersData, error: usersError } = await supabase
        .from('kompakt_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Fetch API keys for all users
      const { data: apiKeysData, error: apiKeysError } = await supabase
        .from('kompakt_api_keys')
        .select('*');

      if (apiKeysError) throw apiKeysError;

      // Fetch analyses count for each user
      const { data: analysesData, error: analysesError } = await supabase
        .from('kompakt_analyses')
        .select('user_id');

      if (analysesError) throw analysesError;

      // Fetch today's usage for each user
      const today = new Date().toISOString().split('T')[0];
      const { data: usageData, error: usageError } = await supabase
        .from('kompakt_usage')
        .select('*')
        .eq('usage_date', today);

      if (usageError) throw usageError;

      // Combine data
      const usersWithStats: UserWithStats[] = (usersData || []).map((user) => {
        const userApiKeys = (apiKeysData || []).filter((key) => key.user_id === user.id);
        const userAnalyses = (analysesData || []).filter((a) => a.user_id === user.id);
        const userUsage = (usageData || []).find((u) => u.user_id === user.id);

        return {
          ...user,
          apiKeys: userApiKeys,
          stats: {
            totalAnalyses: userAnalyses.length,
            todayUsage: userUsage?.count || 0,
          },
        };
      });

      setUsers(usersWithStats);
    } catch (error: any) {
      console.error('Error loading Kompakt users:', error);
      toast({
        title: 'Fehler beim Laden',
        description: error.message || 'Benutzer konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handlePlanChange = async (userId: string, newPlan: 'free' | 'starter' | 'pro' | 'admin') => {
    setUpdatingPlan(userId);
    try {
      // Determine daily limit based on plan
      const dailyLimits: Record<string, number> = {
        free: 3,
        starter: 25,
        pro: 100,
        admin: 999999,
      };

      const { error } = await supabase
        .from('kompakt_users')
        .update({
          plan: newPlan,
          daily_limit: dailyLimits[newPlan],
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: 'Plan aktualisiert',
        description: `Plan wurde erfolgreich auf ${newPlan.toUpperCase()} geändert.`,
      });

      // Refresh users list
      loadUsers();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Plan konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingPlan(null);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    try {
      // Delete in order: usage -> analyses -> api_keys -> user
      const { error: usageError } = await supabase
        .from('kompakt_usage')
        .delete()
        .eq('user_id', userId);

      if (usageError) throw usageError;

      const { error: analysesError } = await supabase
        .from('kompakt_analyses')
        .delete()
        .eq('user_id', userId);

      if (analysesError) throw analysesError;

      const { error: apiKeysError } = await supabase
        .from('kompakt_api_keys')
        .delete()
        .eq('user_id', userId);

      if (apiKeysError) throw apiKeysError;

      const { error: userError } = await supabase
        .from('kompakt_users')
        .delete()
        .eq('id', userId);

      if (userError) throw userError;

      toast({
        title: 'Benutzer gelöscht',
        description: `${userEmail} wurde erfolgreich gelöscht.`,
      });

      // Refresh users list
      loadUsers();
    } catch (error: any) {
      toast({
        title: 'Fehler beim Löschen',
        description: error.message || 'Benutzer konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (user: UserWithStats) => {
    setEditingUser(user);
    setEditPlan(user.plan as 'free' | 'starter' | 'pro' | 'admin');
    setEditDailyLimit(user.daily_limit);
  };

  const saveUserChanges = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    try {
      const { error } = await supabase
        .from('kompakt_users')
        .update({
          plan: editPlan,
          daily_limit: editDailyLimit,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      toast({
        title: 'Benutzer aktualisiert',
        description: `${editingUser.email} wurde erfolgreich aktualisiert.`,
      });

      setEditingUser(null);
      loadUsers();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message || 'Benutzer konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    } finally {
      setSavingUser(false);
    }
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'pro':
        return (
          <Badge className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
            <Crown className="h-3 w-3 mr-1" />
            Pro
          </Badge>
        );
      case 'starter':
        return (
          <Badge className="bg-cyan-600 text-white">
            <Zap className="h-3 w-3 mr-1" />
            Starter
          </Badge>
        );
      case 'admin':
        return (
          <Badge variant="destructive">
            <Key className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        );
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.plan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalUsers = users.length;
  const freeUsers = users.filter((u) => u.plan === 'free').length;
  const starterUsers = users.filter((u) => u.plan === 'starter').length;
  const proUsers = users.filter((u) => u.plan === 'pro').length;

  return (
    <>
    <Tabs defaultValue="users" className="space-y-6">
      <TabsList>
        <TabsTrigger value="users" className="gap-2">
          <Users className="w-4 h-4" />
          Benutzerverwaltung
        </TabsTrigger>
        <TabsTrigger value="branding" className="gap-2">
          <Palette className="w-4 h-4" />
          PDF-Branding
        </TabsTrigger>
      </TabsList>

      <TabsContent value="users" className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" />
              Gesamt Benutzer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Free</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-600">{freeUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Zap className="h-4 w-4 text-cyan-600" />
              Starter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-cyan-600">{starterUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Crown className="h-4 w-4 text-purple-600" />
              Pro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{proUsers}</div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Users className="w-5 h-5" />
                Kompakt Benutzerverwaltung
              </CardTitle>
              <CardDescription>Alle registrierten Klartech Kompakt Benutzer</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadUsers} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
          <div className="relative max-w-md mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Benutzer suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin" />
              <p>Lade Benutzer...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Keine Benutzer gefunden</p>
              <p className="text-sm">
                {searchTerm
                  ? 'Versuchen Sie eine andere Suchanfrage.'
                  : 'Es sind noch keine Kompakt-Benutzer registriert.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">E-Mail</TableHead>
                    <TableHead className="font-semibold">Plan</TableHead>
                    <TableHead className="font-semibold">Limit</TableHead>
                    <TableHead className="font-semibold">API-Keys</TableHead>
                    <TableHead className="font-semibold">Analysen</TableHead>
                    <TableHead className="font-semibold">Heute</TableHead>
                    <TableHead className="font-semibold">Registriert</TableHead>
                    <TableHead className="font-semibold">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className="hover:bg-muted/30">
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>{getPlanBadge(user.plan)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.daily_limit === 999999 ? '∞' : user.daily_limit}/Tag
                      </TableCell>
                      <TableCell>
                        <ApiKeysCell apiKeys={user.apiKeys} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-4 w-4 text-muted-foreground" />
                          <span>{user.stats?.totalAnalyses || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            (user.stats?.todayUsage || 0) >= user.daily_limit && user.daily_limit !== 999999
                              ? 'text-red-600 font-medium'
                              : ''
                          }
                        >
                          {user.stats?.todayUsage || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(user.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => openEditDialog(user)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>

                          <Select
                            value={user.plan}
                            onValueChange={(value) =>
                              handlePlanChange(user.id, value as 'free' | 'starter' | 'pro' | 'admin')
                            }
                            disabled={updatingPlan === user.id}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="starter">Starter</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm" className="h-8 w-8 p-0">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Sind Sie sicher, dass Sie <strong>{user.email}</strong> löschen möchten? Diese Aktion
                                  kann nicht rückgängig gemacht werden. Alle Analysen, API-Keys und Nutzungsdaten werden
                                  permanent gelöscht.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(user.id, user.email)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Endgültig löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="branding">
        <KompaktBrandingEditor />
      </TabsContent>
    </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>
              {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-plan">Plan</Label>
              <Select value={editPlan} onValueChange={(v) => setEditPlan(v as 'free' | 'starter' | 'pro' | 'admin')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-daily-limit">Tägliches Limit</Label>
              <Input
                id="edit-daily-limit"
                type="number"
                value={editDailyLimit}
                onChange={(e) => setEditDailyLimit(parseInt(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">
                Standard: Free=3, Starter=25, Pro=100, Admin=999999
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Abbrechen
            </Button>
            <Button onClick={saveUserChanges} disabled={savingUser}>
              <Save className="h-4 w-4 mr-2" />
              {savingUser ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
